import React, { useEffect, useState } from 'react';
import {
  subscribeInstallState, triggerInstall, bumpVisitCount,
  markDismissed, isRecentlyDismissed,
} from '../lib/pwaInstall';

// Bottom-of-screen install banner. Shows on second+ visit, suppresses for 14
// days after dismissal, hidden when running in standalone mode (already
// installed). On iOS — where beforeinstallprompt isn't supported — we surface
// step-by-step Add-to-Home-Screen instructions instead of a click-to-install
// button.

export default function PWAInstallPrompt() {
  const [state, setState] = useState({ canInstall: false, installed: false, isIOS: false, isStandalone: false });
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visitCount, setVisitCount] = useState(0);

  useEffect(() => {
    setVisitCount(bumpVisitCount());
    setDismissed(isRecentlyDismissed());
    const unsub = subscribeInstallState(setState);
    return unsub;
  }, []);

  if (state.installed || state.isStandalone) return null;
  if (dismissed) return null;
  // Don't pester first-time visitors. Wait until they've actually engaged.
  if (visitCount < 2) return null;
  // Not installable + not iOS → no install path; render nothing.
  if (!state.canInstall && !state.isIOS) return null;

  const onDismiss = () => { markDismissed(); setDismissed(true); setShowIosHelp(false); };
  const onInstall = async () => {
    if (state.isIOS) { setShowIosHelp(true); return; }
    const outcome = await triggerInstall();
    if (outcome === 'dismissed') onDismiss();
  };

  return (
    <>
      <div role="dialog" aria-label="Install SilverGames" style={bannerStyle}>
        <img src="/logo192.png" alt="" width={40} height={40} style={{ borderRadius: 8, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ display: 'block' }}>Install SilverGames</strong>
          <small style={{ color: '#cbd5e1' }}>One-tap drops & queue alerts. Works offline.</small>
        </div>
        <button onClick={onInstall} style={primaryBtn}>Install</button>
        <button onClick={onDismiss} aria-label="Dismiss" style={ghostBtn}>×</button>
      </div>

      {showIosHelp && (
        <div role="dialog" aria-label="Install on iOS" style={iosOverlay} onClick={() => setShowIosHelp(false)}>
          <div style={iosCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.75rem' }}>Add to Home Screen</h3>
            <ol style={{ paddingLeft: '1.25rem', lineHeight: 1.6, color: '#334155' }}>
              <li>Tap the <strong>Share</strong> icon at the bottom of Safari</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong> in the top right</li>
            </ol>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: '0.75rem' }}>
              SilverGames will open like a native app from your home screen, with offline support and push alerts for restocks.
            </p>
            <button onClick={() => setShowIosHelp(false)} style={primaryBtn}>Got it</button>
          </div>
        </div>
      )}
    </>
  );
}

const bannerStyle = {
  position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 16px', borderRadius: 12,
  background: '#0f172a', color: '#f1f5f9',
  boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
  maxWidth: 520, margin: '0 auto', fontSize: 14,
};
const primaryBtn = {
  background: '#3b4cca', color: '#fff', border: 0, borderRadius: 8,
  padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
};
const ghostBtn = {
  background: 'transparent', color: '#94a3b8', border: 0, fontSize: 22,
  cursor: 'pointer', padding: '0 6px',
};
const iosOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const iosCard = {
  background: '#fff', color: '#0f172a', borderRadius: 12,
  padding: '20px 24px', maxWidth: 420, width: '100%',
  boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
};
